import { PanelState } from "config/analysisConfig";
import { PanelComponentKeyType } from "config/panelConfig";
import { getOrthoNormal, getWorldCoordinate, normalize, updateViewportOrientation, updateViewportsWindowLevelWidth, updateViewportToWorld, updateViewportZoom } from "@features/analysis/rendering/Panels/Renderers/CornerstoneManager";
import { useBufferPool } from "@features/analysis/rendering/Panels/Renderers/vtkManagers";
import { usePanelContainer } from "@features/analysis/rendering/Panels/usePanelContainer";
import { useBrowserPage } from "@features/browser/useBrowserPage";
import { configStore } from "config/configStore";
import React from "react";

export interface ChamberInfo {
  normal: [number, number, number];
  right: [number, number, number];
  up: [number, number, number];
  center: [number, number, number];
}

export interface EFEntry {
  percent: number;
  data: Uint8Array;
  chamberInfo: ChamberInfo;
}

export type EFMap = Record<string, EFEntry>;

export function useEFAnalysis(componentKey: PanelComponentKeyType) {
  const {
    selectedStudySeries,
    selectedSeries,
    selectedSeriesImageTag
  } = useBrowserPage();
  const {
    analysisType
  } = usePanelContainer();
  const isEF = analysisType === 'ejectionFraction';
  const selectedSeriesId = selectedSeries?.id;
  const { bufferPool } = useBufferPool();
  const pool = selectedSeriesId ? bufferPool[selectedSeriesId] : undefined;
  const { renderingQuality } = configStore.get().generalSetting;

  const updateEFViewport = React.useCallback(
    (
      index: number,
      handleUpdatePanelState: (key: keyof PanelState | string, value: any) => void
    ) => {
      if (!selectedStudySeries.length) return;
      if (!pool?.EF) return;
      if (!renderingQuality) return;

      const series = selectedStudySeries[index];
      const { EF } = pool;
      if (!EF.data) return;
      const efMap = pool.EF.data as EFMap | undefined;
      if(!efMap) return;
      const efEntry = efMap[series.id];
      if(!efEntry) return;
      const { chamberInfo } = efEntry;

      const { center, normal, up } = chamberInfo;
      const newCenter = center.map((v) => v * 0.5) as typeof center;

      let viewUp = normalize(up.map((v) => -v) as typeof normal);
      let viewPlaneNormal = normal;

      const focalPoint = getWorldCoordinate(componentKey, newCenter, renderingQuality);
      const axes = [
        [0, 0, 0] as [number, number, number],
        normalize(up.map((v) => -v) as typeof normal),
        normal,
      ];
      axes[0] = getOrthoNormal(axes[1], axes[2]);
      switch (componentKey) {
        case 'Axial':
          viewUp = axes[1];
          viewPlaneNormal = axes[2];
          break;
        case 'Coronal':
          viewUp = axes[2];
          viewPlaneNormal = axes[1];
          break;
        case 'Sagittal':
          viewUp = axes[2];
          viewPlaneNormal = axes[0];
          break;
      }

      updateViewportOrientation({
        viewportId: componentKey,
        viewPlaneNormal,
        viewUp,
      });

      updateViewportToWorld(componentKey, focalPoint);
      const windowLevel = Number(selectedSeriesImageTag?.windowCenter);
      const windowWidth = Number(selectedSeriesImageTag?.windowWidth);
      updateViewportsWindowLevelWidth(windowLevel, windowWidth);
      // updateViewportWindowLevelWidth(componentKey, windowLevel, windowWidth);
      requestAnimationFrame(
        () => handleUpdatePanelState('zoom', updateViewportZoom(componentKey, 0)),
      );
    },
    [selectedStudySeries]
  );

  return {
    isEF,
    updateEFViewport,
  };
}
